"use client"

/**
 * One row in the My Listings → Drafts tab. Mirrors ListingCard's row
 * silhouette (flex gap-3 p-2 rounded-2xl, 72px rounded-xl thumb, SQUARE_SM
 * preset) so the tab reads as native next to the listing rows. A draft has
 * no detail page, so the whole row resumes; the explicit pill makes the
 * action discoverable. The `unreadable` arm (future payload version) renders
 * the denormalized title/category/date and offers delete only.
 */

import { ImageIcon, Loader2, Trash2 } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"
import { useMemo } from "react"

import { unreachableCategory, type ListingCategory } from "@kraahl/contract"

import type { ListingDraftListItem } from "@/hooks/use-listing-drafts"
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import { formatPrice } from "@/lib/utils"

function categoryLabelKey(category: ListingCategory): string {
  switch (category) {
    case "lego":
      return "lego"
    case "trading_card":
      return "trading_cards"
    case "comic":
      return "comics"
    default:
      return unreachableCategory(category)
  }
}

interface ListingDraftCardProps {
  item: ListingDraftListItem
  isDeleting: boolean
  onResumeAction: () => void
  onDeleteAction: () => void
}

export function ListingDraftCard({
  item,
  isDeleting,
  onResumeAction,
  onDeleteAction,
}: ListingDraftCardProps) {
  const t = useTranslations("myListings")
  const tCategories = useTranslations("categories")
  const locale = useLocale()

  const readable = item.kind === "readable"
  const price = readable ? item.data.price : undefined
  const currency = readable ? item.data.currency : undefined

  const thumbSrc = useMemo(
    () =>
      item.thumbnailPath
        ? imagePresetUrl(resolveStorageUrl(item.thumbnailPath), SQUARE_SM)
        : null,
    [item.thumbnailPath],
  )

  const updatedLabel = useMemo(() => {
    const date = new Date(item.updatedAt)
    if (Number.isNaN(date.getTime())) return null
    return t("draftUpdatedAt", {
      date: date.toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    })
  }, [item.updatedAt, locale, t])

  return (
    <div
      role={readable ? "button" : undefined}
      tabIndex={readable ? 0 : undefined}
      onClick={readable ? onResumeAction : undefined}
      onKeyDown={
        readable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onResumeAction()
              }
            }
          : undefined
      }
      className="flex gap-3 p-2 rounded-2xl transition-colors hover:bg-muted/50 cursor-pointer"
    >
      <div className="relative shrink-0 w-[72px] h-[72px] rounded-xl overflow-hidden bg-muted">
        {thumbSrc ? (
          // eslint-disable-next-line @next/next/no-img-element -- matches ListingCard's plain <img> row thumbs (preset-resized upstream)
          <img
            src={thumbSrc}
            alt={item.title || t("draftUntitled")}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
        <p className="text-sm font-semibold truncate">
          {item.title.trim() || t("draftUntitled")}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {price != null ? (
            <span className="font-medium text-foreground">
              {formatPrice(price, currency ?? "USD")}
            </span>
          ) : null}
          {price != null && item.listingCategory ? " · " : null}
          {item.listingCategory ? tCategories(categoryLabelKey(item.listingCategory)) : null}
        </p>
        {updatedLabel ? (
          <p className="text-xs text-muted-foreground truncate">{updatedLabel}</p>
        ) : null}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {readable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onResumeAction()
            }}
            className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-[13px] font-medium hover:bg-primary/90"
          >
            {t("draftResume")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDeleteAction()
          }}
          disabled={isDeleting}
          className="h-9 w-9 flex items-center justify-center rounded-full bg-muted hover:bg-muted/70 disabled:opacity-50"
          aria-label={t("delete")}
        >
          {isDeleting ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  )
}
